import { useEffect, lazy, Suspense } from "react";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { AccessProvider } from "@/contexts/AccessContext";
import ScrollToTop from "./components/ScrollToTop";
import AppWeiche from "./components/app/AppWeiche";
import LegacyRedirects from "./components/LegacyRedirects";
import LegacyPageRedirects from "./components/LegacyPageRedirects";

import { useMikoStudioMigration } from "./hooks/useMikoStudioMigration";
import { useAuthSync } from "./hooks/useAuthSync";
import { useLoginTracking } from "./hooks/useLoginTracking";
import { GlobalFloatingTaskButton } from "./components/admin/planning/GlobalFloatingTaskButton";
import { AttributionCapture } from "./components/marketing/AttributionCapture";
// Rückgewinnung (01.11.2026): nach einer Registrierung einmal zurück auf /fuer-dich/<code>, liest nur den Speicher.
import RueckgewinnungNachAnmeldung from "./components/webseite/rueckgewinnung/RueckgewinnungNachAnmeldung";
import Weiterleitung from "./components/Weiterleitung";

// ── Public / Marketing pages (static – important for SEO & first paint) ──
import Index from "./pages/Index";
import Termine from "./pages/Termine";
import Preise from "./pages/Preise";
import Gewinnspiel from "./pages/Gewinnspiel";
import GewinnspielTeilnahmebedingungen from "./pages/GewinnspielTeilnahmebedingungen";
import FachkraftMusik from "./pages/FachkraftMusik";
import FachkraftGitarre from "./pages/FachkraftGitarre";
import FachkraftMusikUndSprache from "./pages/FachkraftMusikUndSprache";
import BundleKaufen from "./pages/BundleKaufen";
import Foerdermoeglichkeiten from "./pages/Foerdermoeglichkeiten";
import PaedagogischeFachkraefte from "./pages/PaedagogischeFachkraefte";
import MusikKita from "./pages/MusikKita";
import UeberUns from "./pages/UeberUns";
import Kontakt from "./pages/Kontakt";
import Hilfe from "./pages/Hilfe";
import Impressum from "./pages/Impressum";
import AGB from "./pages/AGB";
import Datenschutz from "./pages/Datenschutz";
import Widerruf from "./pages/Widerruf";
import VertragWiderrufen from "./pages/VertragWiderrufen";
import VertragBestaetigung from "./pages/VertragBestaetigung";
import Newsletter from "./pages/Newsletter";
import CommunityLanding from "./pages/CommunityLanding";
import KontoLoeschen from "./pages/KontoLoeschen";
import DatenLoeschen from "./pages/DatenLoeschen";
import InhouseSchulungen from "./pages/InhouseSchulungen";
import MusiKonzeptVorOrt from "./pages/MusiKonzeptVorOrt";
import Login from "./pages/Login";
import LoginApp from "./pages/LoginApp";
import Registrieren from "./pages/Registrieren";
import Kostenlos from "./pages/Kostenlos";
import PublicCertificate from "./pages/PublicCertificate";
import NotFound from "./pages/NotFound";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Kurse from "./pages/Kurse";
import Themenreisen from "./pages/Themenreisen";
import Miko from "./pages/Miko";
import PartnerLanding from "./pages/PartnerLanding";
import PartnerDashboard from "./pages/partner/PartnerDashboard";
import PartnerLinks from "./pages/partner/PartnerLinks";
import PartnerAuszahlungen from "./pages/partner/PartnerAuszahlungen";
import PartnerKunden from "./pages/partner/PartnerKunden";
import StudentPartnerprogramm from "./pages/student/StudentPartnerprogramm";
import Didacta from "./pages/Didacta";
import Links from "./pages/Links";
import { WebseiteWeiche } from "./components/webseite/WebseiteWeiche";
import Kinderlieder from "./pages/Kinderlieder";
import KinderliedDetail from "./pages/KinderliedDetail";

// ── Lazy-loaded pages (code-split into separate chunks) ──

// Registration variants
const GastRegistrieren = lazy(() => import("./pages/GastRegistrieren"));
const MusikKitaTeamRegistrieren = lazy(() => import("./pages/MusikKitaTeamRegistrieren"));
const MusikKitaElternRegistrieren = lazy(() => import("./pages/MusikKitaElternRegistrieren"));
const KitaPostAbbestellen = lazy(() => import("./components/labs/pinnwand/KitaPostAbbestellen"));

// Checkout / Purchase pages
const Checkout = lazy(() => import("./pages/Checkout"));
const TerminCheckout = lazy(() => import("./pages/TerminCheckout"));
const ThemenreiseCheckout = lazy(() => import("./pages/ThemenreiseCheckout"));

const AboCheckout = lazy(() => import("./pages/AboCheckout"));
const AboErfolgreich = lazy(() => import("./pages/AboErfolgreich"));
const MusikKitaPaketCheckout = lazy(() => import("./pages/MusikKitaPaketCheckout"));
const MusikKitaPaketErfolgreich = lazy(() => import("./pages/MusikKitaPaketErfolgreich"));
const KaufErfolgreich = lazy(() => import("./pages/KaufErfolgreich"));
const BundleKaufErfolgreich = lazy(() => import("./pages/BundleKaufErfolgreich"));
const TerminGebucht = lazy(() => import("./pages/TerminGebucht"));

// Landing / preview pages
const CourseLandingPage = lazy(() => import("./pages/CourseLandingPage"));
const TerminLandingPage = lazy(() => import("./pages/TerminLandingPage"));
const KursVorschau = lazy(() => import("./pages/KursVorschau"));
const MusikKitaVerzeichnis = lazy(() => import("./pages/MusikKitaVerzeichnis")); // uses mapbox-gl
const AppStart = lazy(() => import("./pages/AppStart"));
const WebinarSlides = lazy(() => import("./pages/WebinarSlides"));
// Webseite neu (Sitzung 9): neue Seiten nur hinter der Weiche, ohne Weiche steht genau die alte Seite.
const WebseiteStartseite = lazy(() => import("./pages/webseite/Startseite"));
const WebseiteMiko = lazy(() => import("./pages/webseite/MikoSeite"));
const WebseiteLinks = lazy(() => import("./pages/webseite/LinksSeite"));
const WebseiteAnmelden = lazy(() => import("./pages/webseite/Anmelden"));
const WebseiteAppStart = lazy(() => import("./pages/webseite/AppStartNeu"));
const WebseiteAnmeldenApp = lazy(() => import("./pages/webseite/AnmeldenApp"));
const WebseitePreise = lazy(() => import("./pages/webseite/PreiseSeite"));
const WebseiteKasse = lazy(() => import("./components/webseite/kasse/KasseWeiche"));
const WebseiteRegistrieren = lazy(() => import("./pages/webseite/RegistrierenNeu"));
const WebseiteElternRegistrieren = lazy(() => import("./pages/webseite/ElternRegistrierenNeu"));
const WebseiteKostenlos = lazy(() => import("./pages/webseite/KostenlosNeu"));
const WebseiteShopifyDanke = lazy(() => import("./pages/webseite/ShopifyDankeNeu"));
const WebseiteHilfe = lazy(() => import("./pages/webseite/HilfeNeu"));
const WebseiteKurse = lazy(() => import("./pages/webseite/KurseNeu"));
const WebseiteKursseite = lazy(() => import("./pages/webseite/KursseiteNeu"));
const WebseiteAusbildung = lazy(() => import("./pages/webseite/AusbildungsseiteNeu"));
const WebseiteAusbildungKaufen = lazy(() => import("./pages/webseite/AusbildungKaufenNeu"));
// Sitzung K2: die übrigen Kassen in der Kassen-Vorlage, Logik wörtlich aus den alten Seiten (kassenK2.test.ts).
const WebseiteAboKasse = lazy(() => import("./pages/webseite/kassen/AboKasseNeu"));
// Sitzung AF (Frage 491 A): eigene Kasse „Dein Webinar-Preis“, im neuen Design ohne Weiche.
const WebinarKasse = lazy(() => import("./pages/webseite/kassen/WebinarKasse"));
const WebseiteKitaKasse = lazy(() => import("./pages/webseite/kassen/KitaKasseNeu"));
const WebseiteKursKasse = lazy(() => import("./pages/webseite/kassen/KursKasseNeu"));
const WebseiteTerminKasse = lazy(() => import("./pages/webseite/kassen/TerminKasseNeu"));
const WebseiteThemenreiseKasse = lazy(() => import("./pages/webseite/kassen/ThemenreiseKasseNeu"));
const WebseiteAusbildungDanke = lazy(() => import("./pages/webseite/AusbildungDankeNeu"));
// Sitzung KM: Dankeseiten nach dem Kauf, Logik wörtlich aus den alten Seiten (dankeseiten.test.ts).
const WebseiteAboDanke = lazy(() => import("./pages/webseite/danke/AboDankeNeu"));
const WebseiteKitaDanke = lazy(() => import("./pages/webseite/danke/KitaDankeNeu"));
const WebseiteKursDanke = lazy(() => import("./pages/webseite/danke/KursDankeNeu"));
const WebseiteTerminDanke = lazy(() => import("./pages/webseite/danke/TerminDankeNeu"));
const WebseiteThemenreiseDanke = lazy(() => import("./pages/webseite/danke/ThemenreiseDankeNeu"));
const WebseiteFoerderung = lazy(() => import("./pages/webseite/FoerderungNeu"));
const WebseiteFachkraefte = lazy(() => import("./pages/webseite/FachkraefteNeu"));
const WebseiteMusikKita = lazy(() => import("./pages/webseite/MusikKitaNeu"));
const WebseiteMusikKitaVerzeichnis = lazy(() => import("./pages/webseite/MusikKitaVerzeichnisNeu"));
const WebseiteVorOrt = lazy(() => import("./pages/webseite/VorOrtNeu"));
const WebseitePartnerWerden = lazy(() => import("./pages/webseite/PartnerWerdenNeu"));
// Bildungspartner (01.11.2026): neue Seite ohne alte Fassung, deshalb ohne Weiche.
const WebseiteBildungspartner = lazy(() => import("./pages/webseite/BildungspartnerNeu"));
// Das neue MusiKonzept (03.11.2026): Infoseite mit dem Video zum Start für die Mail an alle, ohne Weiche, noindex.
const WebseiteDasNeueMusikonzept = lazy(() => import("./pages/webseite/DasNeueMusikonzept"));
// Rückgewinnung (01.11.2026): persönliche Seite zu Claras Mail und ihre Abmeldung, ohne Weiche, noindex.
const FuerDichSeite = lazy(() => import("./pages/webseite/FuerDichSeite"));
const FuerDichAbmelden = lazy(() => import("./pages/webseite/FuerDichAbmelden"));
const WebseiteTermine = lazy(() => import("./pages/webseite/TermineNeu"));
const WebseiteTerminseite = lazy(() => import("./pages/webseite/TerminseiteNeu"));
const WebseiteKinderlieder = lazy(() => import("./pages/webseite/KinderliederNeu"));
const WebseiteKinderlied = lazy(() => import("./pages/webseite/KinderliedNeu"));
const WebseiteNewsletter = lazy(() => import("./pages/webseite/NewsletterNeu"));
const WebseiteKontakt = lazy(() => import("./pages/webseite/KontaktNeu"));
const WebseiteBlog = lazy(() => import("./pages/webseite/BlogNeu"));
const WebseiteBlogArtikel = lazy(() => import("./pages/webseite/BlogArtikelNeu"));
const WebseiteCommunity = lazy(() => import("./pages/webseite/CommunityNeu"));
const WebseiteUeberUns = lazy(() => import("./pages/webseite/UeberUnsNeu"));
const WebseiteMusiKonzeptag = lazy(() => import("./pages/webseite/MusiKonzeptagNeu"));
const WebseiteWebinar = lazy(() => import("./pages/webseite/WebinarNeu"));
const WebseiteWebinarBestaetigung = lazy(() => import("./pages/webseite/WebinarBestaetigungNeu"));
const WebseiteWebinarNachschau = lazy(() => import("./pages/webseite/WebinarNachschauNeu"));
// Infoveranstaltungen (Rohling 21.10.2026): neue Seiten ohne alte Fassung, deshalb ohne Weiche.
const InfoveranstaltungSeite = lazy(() => import("./pages/webseite/InfoveranstaltungSeite"));
const InfoveranstaltungAbmelden = lazy(() => import("./pages/webseite/InfoveranstaltungAbmelden"));
const StreckeAbmelden = lazy(() => import("./pages/webseite/StreckeAbmelden"));
// Automatisches Webinar (W1): Seite zum Ansehen und Passwort aus der ersten Mail, ohne alte Fassung.
const WebinarAnsehen = lazy(() => import("./pages/webseite/WebinarAnsehen"));
const PasswortFestlegen = lazy(() => import("./pages/webseite/PasswortFestlegen"));
const WebseiteThemenreisen = lazy(() => import("./pages/webseite/ThemenreisenNeu"));
const WebseiteThemenreise = lazy(() => import("./pages/webseite/ThemenreiseNeu"));
const DidactaWebinar = lazy(() => import("./pages/DidactaWebinar"));
const DidactaAktion = lazy(() => import("./pages/DidactaAktion"));
const WebinarNachschau = lazy(() => import("./pages/WebinarNachschau"));
const WebinarKitaGitarrenkursNachschau = lazy(() => import("./pages/WebinarKitaGitarrenkursNachschau"));
const WebinarFachkraftMusikNachschau = lazy(() => import("./pages/WebinarFachkraftMusikNachschau"));
const CallcenterCheckout = lazy(() => import("./pages/CallcenterCheckout"));
const Comeback = lazy(() => import("./pages/Comeback"));
const Angebot = lazy(() => import("./pages/Angebot"));
const ShopifyDanke = lazy(() => import("./pages/ShopifyDanke"));

// Auth
const ImpersonateCallback = lazy(() => import("./pages/auth/ImpersonateCallback"));
const AuthVerify = lazy(() => import("./pages/AuthVerify"));

// ── Admin pages ──
const Admin = lazy(() => import("./pages/Admin"));

const AdminSetup = lazy(() => import("./pages/AdminSetup"));
const AdminRahmen = lazy(() => import("./components/admin/AdminRahmen"));
const AdminGewinnspiel = lazy(() => import("./pages/admin/AdminGewinnspiel"));
const AdminAffiliates = lazy(() => import("./pages/admin/AdminAffiliates"));
const AdminAffiliatePayouts = lazy(() => import("./pages/admin/AdminAffiliatePayouts"));
const AdminAffiliateDetail = lazy(() => import("./pages/admin/AdminAffiliateDetail"));
const AdminOverview = lazy(() => import("./pages/admin/AdminOverview"));
const AdminTermine = lazy(() => import("./pages/admin/AdminTermine"));
const AdminTerminEdit = lazy(() => import("./pages/admin/AdminTerminEdit"));
const AdminKursleiterTermine = lazy(() => import("./pages/admin/AdminKursleiterTermine"));
const AdminAusbildungsTermine = lazy(() => import("./pages/admin/AdminAusbildungsTermine"));
const AdminAktuelles = lazy(() => import("./pages/admin/AdminAktuelles"));
const AdminKontaktanfragen = lazy(() => import("./pages/admin/AdminKontaktanfragen"));
const AdminKurse = lazy(() => import("./pages/admin/AdminKurse"));
const AdminMeineKurse = lazy(() => import("./pages/admin/AdminMeineKurse"));
const AdminKursEdit = lazy(() => import("./pages/admin/AdminKursEdit"));
const AdminProjektnotizen = lazy(() => import("./pages/admin/AdminProjektnotizen"));
const AdminWissensdatenbank = lazy(() => import("./pages/admin/AdminWissensdatenbank"));
const AdminAufgabenBoard = lazy(() => import("./pages/admin/AdminAufgabenBoard"));
const AdminPlanungszentrale = lazy(() => import("./pages/admin/AdminPlanungszentrale"));
const AdminKursleiter = lazy(() => import("./pages/admin/AdminKursleiter"));
const AdminNutzer = lazy(() => import("./pages/admin/AdminNutzer"));
const AdminNutzerDetail = lazy(() => import("./pages/admin/AdminNutzerDetail"));
const AdminBundles = lazy(() => import("./pages/admin/AdminBundles"));
const AdminBundleDetail = lazy(() => import("./pages/admin/AdminBundleDetail"));
const AdminLernbereiche = lazy(() => import("./pages/admin/AdminLernbereiche"));
const AdminThemenreisen = lazy(() => import("./pages/admin/AdminThemenreisen"));
const AdminLernbereichDetail = lazy(() => import("./pages/admin/AdminLernbereichDetail"));
const AdminMusikKita = lazy(() => import("./pages/admin/AdminMusikKita"));
const AdminInfoKacheln = lazy(() => import("./pages/admin/AdminInfoKacheln"));
const AdminEmails = lazy(() => import("./pages/admin/AdminEmails"));
const AdminEmailDesigner = lazy(() => import("./pages/admin/AdminEmailDesigner"));
const AdminDateien = lazy(() => import("./pages/admin/AdminDateien"));
const AdminThinkificImport = lazy(() => import("./pages/admin/AdminThinkificImport"));
const AdminThinkificNutzer = lazy(() => import("./pages/admin/AdminThinkificNutzer"));
const AdminMediaMigration = lazy(() => import("./pages/admin/AdminMediaMigration"));
const AdminKIModelle = lazy(() => import("./pages/admin/AdminKIModelle"));
// Miko Labs – Testbereich, Zugang nur über labs_zugang (docs/labs.md)
const LabsUebersicht = lazy(() => import("./pages/admin/labs/LabsUebersicht"));
const LabsFunktion = lazy(() => import("./pages/admin/labs/LabsFunktion"));
const LaborMitgliederbereich = lazy(() => import("./pages/admin/labs/LaborMitgliederbereich"));
const NeueAnsichtWerkzeugRoute = lazy(() => import("./components/labs/neueAnsicht/NeueAnsichtWerkzeugRoute"));
// Ziel der QR-Codes auf Spickkarte, Ablaufblatt und Gesprächs-PDF (Weiterleitung mit Anmeldung, src/lib/labs/qrLink.ts)
const LabsQrWeiter = lazy(() => import("./pages/admin/labs/LabsQrWeiter"));
const AdminAufgaben = lazy(() => import("./pages/admin/AdminAufgaben"));
const AdminSuchverwaltung = lazy(() => import("./pages/admin/AdminSuchverwaltung"));
const AdminThinkificInstructors = lazy(() => import("./pages/admin/AdminThinkificInstructors"));
const AdminThinkificBundles = lazy(() => import("./pages/admin/AdminThinkificBundles"));
const AdminTransaktionen = lazy(() => import("./pages/admin/AdminTransaktionen"));
const AdminRechnungskorrektur = lazy(() => import("./pages/admin/AdminRechnungskorrektur"));
const AdminZahlungsprobleme = lazy(() => import("./pages/admin/AdminZahlungsprobleme"));
const AdminMahnwesen = lazy(() => import("./pages/admin/AdminMahnwesen"));
const AdminUmsatz = lazy(() => import("./pages/admin/AdminUmsatz"));
const AdminProfilStatistiken = lazy(() => import("./pages/admin/AdminProfilStatistiken"));
const AdminWebinarAnmeldungen = lazy(() => import("./pages/admin/AdminWebinarAnmeldungen"));
const AdminPraesentationen = lazy(() => import("./pages/admin/AdminPraesentationen"));
const AdminAusbildungTeilnehmer = lazy(() => import("./pages/admin/AdminAusbildungTeilnehmer"));
const AdminProvisionen = lazy(() => import("./pages/admin/AdminProvisionen"));
const AdminAusbildungBewertungen = lazy(() => import("./pages/admin/AdminAusbildungBewertungen"));
const BewertungEntscheiden = lazy(() => import("./pages/BewertungEntscheiden"));
const AdminMeineProvisionen = lazy(() => import("./pages/admin/AdminMeineProvisionen"));
const AdminAusbildungTeilnehmerDetail = lazy(() => import("./pages/admin/AdminAusbildungTeilnehmerDetail"));
const AdminAusbildungTeilnehmerImport = lazy(() => import("./pages/admin/AdminAusbildungTeilnehmerImport"));
const AdminAusbildungTeilnehmerConsolidation = lazy(() => import("./pages/admin/AdminAusbildungTeilnehmerConsolidation"));
const AdminKonsistenzReport = lazy(() => import("./pages/admin/AdminKonsistenzReport"));
const AdminLaeuftAlles = lazy(() => import("./pages/admin/AdminLaeuftAlles"));
const AdminKontakte = lazy(() => import("./pages/admin/AdminKontakte"));
const AdminKontaktAkte = lazy(() => import("./pages/admin/AdminKontaktAkte"));
const AdminFlows = lazy(() => import("./pages/admin/AdminFlows"));
const AdminInfoveranstaltungen = lazy(() => import("./pages/admin/AdminInfoveranstaltungen"));
const AdminInfoveranstaltung = lazy(() => import("./pages/admin/AdminInfoveranstaltung"));
const AdminPipeline = lazy(() => import("./pages/admin/AdminPipeline"));
const AdminEinrichtungen = lazy(() => import("./pages/admin/AdminEinrichtungen"));
const AdminTerminReihen = lazy(() => import("./pages/admin/AdminTerminReihen"));
const AdminTerminKalender = lazy(() => import("./pages/admin/AdminTerminKalender"));
const AdminEinrichtungAkte = lazy(() => import("./pages/admin/AdminEinrichtungAkte"));
const AdminAboEinstellungen = lazy(() => import("./pages/admin/AdminAboEinstellungen"));
const AdminKursRotation = lazy(() => import("./pages/admin/AdminKursRotation"));
const AdminPruefungen = lazy(() => import("./pages/admin/AdminPruefungen"));
const AdminPruefungDetail = lazy(() => import("./pages/admin/AdminPruefungDetail"));
const AdminKategorien = lazy(() => import("./pages/admin/AdminKategorien"));
const AdminHelpArticles = lazy(() => import("./pages/admin/AdminHelpArticles"));
const AdminSupportTickets = lazy(() => import("./pages/admin/AdminSupportTickets"));
const AdminTicketDetail = lazy(() => import("./pages/admin/AdminTicketDetail"));
const AdminCommunity = lazy(() => import("./pages/admin/AdminCommunity"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminDidactaStats = lazy(() => import("./pages/admin/AdminDidactaStats"));
const AdminNewUserStats = lazy(() => import("./pages/admin/AdminNewUserStats"));
const AdminMitgliederStats = lazy(() => import("./pages/admin/AdminMitgliederStats"));
const AdminMitgliederAbos = lazy(() => import("./pages/admin/AdminMitgliederAbos"));
const AdminLandingPages = lazy(() => import("./pages/admin/AdminLandingPages"));
const AdminAngebote = lazy(() => import("./pages/admin/AdminAngebote"));
const AdminRueckgewinnung = lazy(() => import("./pages/admin/AdminRueckgewinnung"));
const AdminProbeAbo = lazy(() => import("./pages/admin/AdminProbeAbo"));
const AdminFortbildungen = lazy(() => import("./pages/admin/AdminFortbildungen"));
const AdminFortbildungDetail = lazy(() => import("./pages/admin/AdminFortbildungDetail"));
const FortbildungPresent = lazy(() => import("./pages/FortbildungPresent"));
const Giveaway = lazy(() => import("./pages/Giveaway"));

// ── Student pages ──
const StudentDashboard = lazy(() => import("./pages/student/StudentDashboard"));
const StudentCourses = lazy(() => import("./pages/student/StudentCourses"));
const StudentCourseView = lazy(() => import("./pages/student/StudentCourseView"));
const StudentFavorites = lazy(() => import("./pages/student/StudentFavorites"));
const StudentSettings = lazy(() => import("./pages/student/StudentSettings"));
const StudentSearch = lazy(() => import("./pages/student/StudentSearch"));
const StudentLearningArea = lazy(() => import("./pages/student/StudentLearningArea"));
const StudentCertificates = lazy(() => import("./pages/student/StudentCertificates")); // uses jspdf
const StudentJourneyPage = lazy(() => import("./pages/student/StudentJourneyPage"));
const StudentThemenreise = lazy(() => import("./pages/student/StudentThemenreise"));
const StudentThemenreisenOverview = lazy(() => import("./pages/student/StudentThemenreisenOverview"));
const ThemenreiseLanding = lazy(() => import("./pages/ThemenreiseLanding"));
const ThemenreiseErfolgreich = lazy(() => import("./pages/ThemenreiseErfolgreich"));
const StudentCategory = lazy(() => import("./pages/student/StudentCategory"));
const StudentTermine = lazy(() => import("./pages/student/StudentTermine"));
const StudentCommunity = lazy(() => import("./pages/student/StudentCommunity"));
const CommunityMembers = lazy(() => import("./pages/student/CommunityMembers"));
const StudentHelp = lazy(() => import("./pages/student/StudentHelp"));
const StudentHelpArticle = lazy(() => import("./pages/student/StudentHelpArticle"));
const StudentTickets = lazy(() => import("./pages/student/StudentTickets"));
const StudentTicketDetail = lazy(() => import("./pages/student/StudentTicketDetail"));
const StudentCreateTicket = lazy(() => import("./pages/student/StudentCreateTicket"));

// ── Miko AI pages (use jspdf/html2canvas) ──
const MikoTools = lazy(() => import("./pages/student/MikoTools"));
const MikoBibliothek = lazy(() => import("./pages/student/MikoBibliothek"));
const MikoChatbot = lazy(() => import("./pages/student/MikoChatbot"));
const MikoAktionstablett = lazy(() => import("./pages/student/MikoAktionstablett"));
const MikoAngebotsAssistent = lazy(() => import("./pages/student/MikoAngebotsAssistent"));
const MikoEingewoehnung = lazy(() => import("./pages/student/MikoEingewoehnung"));
const MikoGeraeuscheSafari = lazy(() => import("./pages/student/MikoGeraeuscheSafari"));
const MikoGeschichtensaeckchen = lazy(() => import("./pages/student/MikoGeschichtensaeckchen"));
const MikoKlanggeschichten = lazy(() => import("./pages/student/MikoKlanggeschichten"));
const MikoNaturmaterialien = lazy(() => import("./pages/student/MikoNaturmaterialien"));
const MikoKulturen = lazy(() => import("./pages/student/MikoKulturen"));
const MikoRhythmusDetektive = lazy(() => import("./pages/student/MikoRhythmusDetektive"));
const MikoVideoIdeen = lazy(() => import("./pages/student/MikoVideoIdeen"));
const MikoUploadIdeen = lazy(() => import("./pages/student/MikoUploadIdeen"));
const MikoFussballGenerator = lazy(() => import("./pages/student/MikoFussballGenerator"));

// ── Hidden landing pages ──
const WebinarFachkraftMusik = lazy(() => import("./pages/WebinarFachkraftMusik"));
const WebinarBestaetigung = lazy(() => import("./pages/WebinarBestaetigung"));
const WebinarKitaGitarrenkurs = lazy(() => import("./pages/WebinarKitaGitarrenkurs"));
const WebinarKitaGitarrenkursBestaetigung = lazy(() => import("./pages/WebinarKitaGitarrenkursBestaetigung"));
const MusiKonzeptag = lazy(() => import("./pages/MusiKonzeptag"));

// Trigger rebuild - kurse routes
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: 1,
    },
  },
});

const AuthRecoveryRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const hash = window.location.hash || "";
    if (hash.includes("type=recovery") && location.pathname !== "/login") {
      navigate({ pathname: "/login", hash }, { replace: true });
    }
  }, [location.pathname, navigate]);

  return null;
};

const MikoStudioMigrationHandler = () => {
  useMikoStudioMigration();
  return null;
};

/** Keeps React Query caches in sync with Supabase auth state changes. */
const AuthSyncHandler = () => {
  useAuthSync();
  return null;
};

/** Loggt pro Browser-Session einen login_events-Eintrag (DAU-Tracking). */
const LoginTrackingHandler = () => {
  useLoginTracking();
  return null;
};

/** Erfasst ?ref=<code> beim ersten Seitenaufruf und persistiert die Attribution. */
const AffiliateRefHandler = () => {
  useEffect(() => {
    import("@/lib/affiliate/tracking").then(({ captureAffiliateRefFromUrl }) => {
      captureAffiliateRefFromUrl().catch(() => {
        /* tracking darf nie blockieren */
      });
    });
  }, []);
  return null;
};

const LazyFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const App = () => (
  <BrowserRouter>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <HelmetProvider>
          <SubscriptionProvider>
            <AccessProvider>
              <Toaster />
              <Sonner />
              <ScrollToTop />
              <AppWeiche />
              <AuthRecoveryRedirect />
              
              <MikoStudioMigrationHandler />
              <AuthSyncHandler />
              <LoginTrackingHandler />
              <AffiliateRefHandler />
              <AttributionCapture />
              <RueckgewinnungNachAnmeldung />
              <GlobalFloatingTaskButton />
              <ErrorBoundary>
              <Suspense fallback={<LazyFallback />}>
              <Routes>
            {/* Web2Native App Entry Point */}
            <Route path="/app" element={<WebseiteWeiche alt={<AppStart />} neu={<WebseiteAppStart />} />} />
            <Route path="/" element={<WebseiteWeiche alt={<Index />} neu={<WebseiteStartseite />} />} />
            <Route path="/info/:slug" element={<InfoveranstaltungSeite />} />
            <Route path="/info/abmelden/:token" element={<InfoveranstaltungAbmelden />} />
            <Route path="/info/:slug/ansehen" element={<WebinarAnsehen />} />
            <Route path="/passwort-festlegen" element={<PasswortFestlegen />} />
            {/* Abmelden von den Strecken-Mails, ohne Anmeldung (KS1) */}
            <Route path="/abmelden" element={<StreckeAbmelden />} />
            <Route path="/webinar/fachkraft-musik" element={<WebseiteWeiche alt={<WebinarFachkraftMusik />} neu={<WebseiteWebinar slug="fachkraft-musik" />} />} />
            <Route path="/webinar/fachkraft-musik/bestaetigung" element={<WebseiteWeiche alt={<WebinarBestaetigung />} neu={<WebseiteWebinarBestaetigung slug="fachkraft-musik" />} />} />
            <Route path="/webinar/kita-gitarrenkurs" element={<WebseiteWeiche alt={<WebinarKitaGitarrenkurs />} neu={<WebseiteWebinar slug="kita-gitarrenkurs" />} />} />
            <Route path="/webinar/kita-gitarrenkurs/bestaetigung" element={<WebseiteWeiche alt={<WebinarKitaGitarrenkursBestaetigung />} neu={<WebseiteWebinarBestaetigung slug="kita-gitarrenkurs" />} />} />
            <Route path="/musikonzeptag" element={<WebseiteWeiche alt={<MusiKonzeptag />} neu={<WebseiteMusiKonzeptag />} />} />
            <Route path="/termin/:slug" element={<WebseiteWeiche alt={<TerminLandingPage />} neu={<WebseiteTerminseite />} />} />
            <Route path="/themenreise/:slug" element={<WebseiteWeiche alt={<ThemenreiseLanding />} neu={<WebseiteThemenreise />} />} />
            <Route path="/themenreisen" element={<WebseiteWeiche alt={<Themenreisen />} neu={<WebseiteThemenreisen />} />} />
            <Route path="/themenreise-erfolgreich" element={<WebseiteWeiche alt={<ThemenreiseErfolgreich />} neu={<WebseiteThemenreiseDanke />} />} />
            <Route path="/termine" element={<WebseiteWeiche alt={<Termine />} neu={<WebseiteTermine />} />} />
            <Route path="/preise" element={<WebseiteWeiche alt={<Preise />} neu={<WebseitePreise />} />} />
            {/* Neue Kasse für Kompakt und Taler-Pakete (K1), Weiche in der Seite selbst (`useKasseNeu()`). */}
            <Route path="/kasse/:produkt" element={<WebseiteKasse />} />
            <Route path="/gewinnspiel" element={<Gewinnspiel />} />
            <Route path="/gewinnspiel/teilnahmebedingungen" element={<GewinnspielTeilnahmebedingungen />} />
            <Route path="/ausbildung/fachkraft-musik" element={<WebseiteWeiche alt={<FachkraftMusik />} neu={<WebseiteAusbildung slug="fachkraft-musik" />} />} />
            <Route path="/ausbildung/fachkraft-musik/sprachbildung" element={<Navigate to="/ausbildung/fachkraft-musik-und-sprache" replace />} />
            <Route path="/ausbildung/fachkraft-musik-und-sprache" element={<WebseiteWeiche alt={<FachkraftMusikUndSprache />} neu={<WebseiteAusbildung slug="fachkraft-musik-und-sprache" />} />} />
            <Route path="/foerdermoeglichkeiten" element={<WebseiteWeiche alt={<Foerdermoeglichkeiten />} neu={<WebseiteFoerderung />} />} />
            <Route path="/ausbildung/fachkraft-gitarre" element={<WebseiteWeiche alt={<FachkraftGitarre />} neu={<WebseiteAusbildung slug="fachkraft-gitarre" />} />} />
            {/* Self-Service Bundle-Checkout (Schritt 8). Nicht öffentlich verlinkt;
                Page guarded gegen inaktive Bundles. */}
            <Route path="/ausbildung/:slug/kaufen" element={<WebseiteWeiche alt={<BundleKaufen />} neu={<WebseiteAusbildungKaufen />} />} />
            <Route path="/ausbildung/:slug/kauf-erfolgreich" element={<WebseiteWeiche alt={<BundleKaufErfolgreich />} neu={<WebseiteAusbildungDanke />} />} />
            <Route path="/paedagogische-fachkraefte" element={<WebseiteWeiche alt={<PaedagogischeFachkraefte />} neu={<WebseiteFachkraefte />} />} />
            <Route path="/musik-kita" element={<WebseiteWeiche alt={<MusikKita />} neu={<WebseiteMusikKita />} />} />
            <Route path="/musik-kita-paket" element={<WebseiteWeiche alt={<MusikKitaPaketCheckout />} neu={<WebseiteKitaKasse />} />} />
            <Route path="/musik-kita-paket-erfolgreich" element={<WebseiteWeiche alt={<MusikKitaPaketErfolgreich />} neu={<WebseiteKitaDanke />} />} />
            <Route path="/musik-kita-verzeichnis" element={<WebseiteWeiche alt={<MusikKitaVerzeichnis />} neu={<WebseiteMusikKitaVerzeichnis />} />} />

            <Route path="/partner-werden" element={<WebseiteWeiche alt={<PartnerLanding />} neu={<WebseitePartnerWerden />} />} />
            <Route path="/partner/werden" element={<Navigate to="/partner-werden" replace />} />
            <Route path="/bildungspartner" element={<WebseiteBildungspartner />} />
            <Route path="/das-neue-musikonzept" element={<WebseiteDasNeueMusikonzept />} />
            <Route path="/fuer-dich/:code" element={<FuerDichSeite />} />
            <Route path="/fuer-dich/:code/abmelden" element={<FuerDichAbmelden />} />
            <Route path="/partner" element={<PartnerDashboard />} />
            <Route path="/partner/kunden" element={<PartnerKunden />} />
            <Route path="/partner/links" element={<PartnerLinks />} />
            <Route path="/partner/auszahlungen" element={<PartnerAuszahlungen />} />
            <Route path="/ueber-uns" element={<WebseiteWeiche alt={<UeberUns />} neu={<WebseiteUeberUns />} />} />
            <Route path="/kontakt" element={<WebseiteWeiche alt={<Kontakt />} neu={<WebseiteKontakt />} />} />
            <Route path="/hilfe" element={<WebseiteWeiche alt={<Hilfe />} neu={<WebseiteHilfe />} />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/agb" element={<AGB />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            {/* Widerruf und Kündigung ohne Anmeldung (Fußzeile „Rechtliches“, Sitzung „Aufräumen“, 14.10.2026) */}
            <Route path="/widerruf" element={<Widerruf />} />
            {/* Gekündigt wird im Konto über „Abo kündigen“ (Daniel, 15.10.2026); der kurz veröffentlichte Link führt dorthin */}
            <Route path="/vertrag-kuendigen" element={<Weiterleitung nach="/mein-bereich/einstellungen" />} />
            <Route path="/vertrag-widerrufen" element={<VertragWiderrufen />} />
            {/* Post von der Kita abbestellen, ohne Anmeldung (Sitzung P3) */}
            <Route path="/kita-post-abbestellen" element={<KitaPostAbbestellen />} />
            <Route path="/vertrag-widerrufen/bestaetigung" element={<VertragBestaetigung />} />
            <Route path="/newsletter" element={<WebseiteWeiche alt={<Newsletter />} neu={<WebseiteNewsletter />} />} />
            <Route path="/konto-loeschen" element={<KontoLoeschen />} />
            <Route path="/daten-loeschen" element={<DatenLoeschen />} />
            <Route path="/community" element={<WebseiteWeiche alt={<CommunityLanding />} neu={<WebseiteCommunity />} />} />
            <Route path="/inhouse-schulungen" element={<WebseiteWeiche alt={<InhouseSchulungen />} neu={<WebseiteVorOrt art="fortbildung" />} />} />
            <Route path="/musikonzept-vor-ort" element={<WebseiteWeiche alt={<MusiKonzeptVorOrt />} neu={<WebseiteVorOrt art="veranstaltung" />} />} />
            <Route path="/blog" element={<WebseiteWeiche alt={<Blog />} neu={<WebseiteBlog />} />} />
            <Route path="/blog/:slug" element={<WebseiteWeiche alt={<BlogPost />} neu={<WebseiteBlogArtikel />} />} />
            <Route path="/kurse" element={<WebseiteWeiche alt={<Kurse />} neu={<WebseiteKurse />} />} />
            <Route path="/kurse/:slug" element={<WebseiteWeiche alt={<CourseLandingPage />} neu={<WebseiteKursseite />} />} />
            <Route path="/miko" element={<WebseiteWeiche alt={<Miko />} neu={<WebseiteMiko />} />} />
            <Route path="/didacta" element={<Didacta />} />
            <Route path="/links" element={<WebseiteWeiche alt={<Links />} neu={<WebseiteLinks />} />} />
            <Route path="/kinderlieder" element={<WebseiteWeiche alt={<Kinderlieder />} neu={<WebseiteKinderlieder />} />} />
            <Route path="/kinderlieder/:slug" element={<WebseiteWeiche alt={<KinderliedDetail />} neu={<WebseiteKinderlied />} />} />
            <Route path="/webinar/nachschau" element={<WebinarNachschau />} />
            <Route path="/webinar/kita-gitarrenkurs/nachschau" element={<WebseiteWeiche alt={<WebinarKitaGitarrenkursNachschau />} neu={<WebseiteWebinarNachschau slug="kita-gitarrenkurs" />} />} />
            <Route path="/webinar/fachkraft-musik/nachschau" element={<WebseiteWeiche alt={<WebinarFachkraftMusikNachschau />} neu={<WebseiteWebinarNachschau slug="fachkraft-musik" />} />} />
            <Route path="/didacta-webinar" element={<DidactaWebinar />} />
            <Route path="/didacta-aktion" element={<DidactaAktion />} />
            <Route path="/kita-testzugang" element={<CallcenterCheckout />} />
            <Route path="/comeback" element={<Comeback />} />
            <Route path="/angebot/:token" element={<Angebot />} />
            <Route path="/shopify-danke" element={<WebseiteWeiche alt={<ShopifyDanke />} neu={<WebseiteShopifyDanke />} />} />
            <Route path="/login" element={<WebseiteWeiche alt={<Login />} neu={<WebseiteAnmelden />} />} />
            <Route path="/qr/:kurs/:lektion" element={<LabsQrWeiter />} />
            <Route path="/qr/:kurs" element={<LabsQrWeiter />} />
            <Route path="/login-app" element={<WebseiteWeiche alt={<LoginApp />} neu={<WebseiteAnmeldenApp />} />} />
            <Route path="/registrieren" element={<WebseiteWeiche alt={<Registrieren />} neu={<WebseiteRegistrieren />} />} />
            <Route path="/zertifikat/:token" element={<PublicCertificate />} />
            <Route path="/bewertung-entscheiden" element={<BewertungEntscheiden />} />
            <Route path="/kostenlos" element={<WebseiteWeiche alt={<Kostenlos />} neu={<WebseiteKostenlos />} />} />
<Route path="/registrieren/gast/:token" element={<GastRegistrieren />} />
            <Route path="/registrieren/musik-kita/:token" element={<MusikKitaTeamRegistrieren />} />
            <Route path="/registrieren/musik-kita-eltern" element={<WebseiteWeiche alt={<MusikKitaElternRegistrieren />} neu={<WebseiteElternRegistrieren />} />} />
            <Route path="/auth/impersonate-callback" element={<ImpersonateCallback />} />
            <Route path="/auth/verify" element={<AuthVerify />} />
            <Route path="/admin/setup" element={<AdminSetup />} />
            <Route path="/admin" element={<Navigate to="/login" replace />} />
            {/* Das Labor zeigt den Mitgliederbereich im Vollbild, ohne Seitenleiste der Administration. */}
            <Route path="/admin/dashboard/labs/labor/*" element={<LaborMitgliederbereich />} />
            {/* Admin-Rahmen Anfang: Seitenleiste und Kopfzeile bleiben beim Seitenwechsel stehen
                (AdminRahmen, docs/strategie/admin-dashboard.md 11.1). Jede neue Admin-Seite gehört hier
                hinein und in src/lib/admin/navigation.ts; Test src/lib/admin/__tests__/navigation.test.ts. */}
            <Route element={<AdminRahmen />}>
              <Route path="/admin/dashboard/gewinnspiel" element={<AdminGewinnspiel />} />
              <Route path="/admin/dashboard/affiliates" element={<AdminAffiliates />} />
              <Route path="/admin/dashboard/affiliates/payouts" element={<AdminAffiliatePayouts />} />
              <Route path="/admin/dashboard/affiliates/:id" element={<AdminAffiliateDetail />} />
              <Route path="/admin/dashboard" element={<AdminOverview />} />
              <Route path="/admin/dashboard/pruefungen" element={<AdminPruefungen />} />
              <Route path="/admin/dashboard/pruefungen/:submissionId" element={<AdminPruefungDetail />} />
              <Route path="/admin/dashboard/termine" element={<AdminTermine />} />
              <Route path="/admin/dashboard/termine/:id" element={<AdminTerminEdit />} />
              <Route path="/admin/dashboard/termin-reihen" element={<AdminTerminReihen />} />
              <Route path="/admin/dashboard/termin-kalender" element={<AdminTerminKalender />} />
              <Route path="/admin/dashboard/meine-termine" element={<AdminKursleiterTermine />} />
              <Route path="/admin/dashboard/meine-ausbildungstermine" element={<AdminAusbildungsTermine />} />
              <Route path="/admin/dashboard/anmeldungen" element={<AdminWebinarAnmeldungen />} />
              <Route path="/admin/dashboard/praesentationen" element={<AdminPraesentationen />} />
              <Route path="/admin/dashboard/aktuelles" element={<AdminAktuelles />} />
              <Route path="/admin/dashboard/kontaktanfragen" element={<AdminKontaktanfragen />} />
              <Route path="/admin/dashboard/meine-kurse" element={<AdminMeineKurse />} />
              <Route path="/admin/dashboard/kurse" element={<AdminKurse />} />
              <Route path="/admin/dashboard/kurse/:id" element={<AdminKursEdit />} />
              <Route path="/admin/dashboard/kursleiter" element={<AdminKursleiter />} />
              <Route path="/admin/dashboard/nutzer" element={<AdminNutzer />} />
              <Route path="/admin/dashboard/nutzer/:userId" element={<AdminNutzerDetail />} />
              <Route path="/admin/dashboard/projektnotizen" element={<AdminProjektnotizen />} />
              <Route path="/admin/dashboard/wissensdatenbank" element={<AdminWissensdatenbank />} />
              <Route path="/admin/dashboard/aufgaben-board-legacy" element={<AdminAufgabenBoard />} />
              <Route path="/admin/dashboard/planungszentrale" element={<AdminPlanungszentrale />} />
              <Route path="/admin/dashboard/bundles" element={<AdminBundles />} />
              <Route path="/admin/dashboard/bundles/:id" element={<AdminBundleDetail />} />
              <Route path="/admin/fortbildungen" element={<AdminFortbildungen />} />
              <Route path="/admin/fortbildungen/:id" element={<AdminFortbildungDetail />} />
              <Route path="/admin/dashboard/lernbereiche" element={<AdminLernbereiche />} />
              <Route path="/admin/dashboard/lernbereiche/:id" element={<AdminLernbereichDetail />} />
              <Route path="/admin/dashboard/themenreisen" element={<AdminThemenreisen />} />
              <Route path="/admin/dashboard/kategorien" element={<AdminKategorien />} />
              <Route path="/admin/dashboard/musik-kita" element={<AdminMusikKita />} />
              <Route path="/admin/dashboard/infokacheln" element={<AdminInfoKacheln />} />
              <Route path="/admin/dashboard/emails" element={<AdminEmails />} />
              <Route path="/admin/dashboard/emails/designer" element={<AdminEmailDesigner />} />
              <Route path="/admin/dashboard/dateien" element={<AdminDateien />} />
              <Route path="/admin/dashboard/thinkific-kurse" element={<AdminThinkificImport />} />
              <Route path="/admin/dashboard/thinkific-nutzer" element={<AdminThinkificNutzer />} />
              <Route path="/admin/dashboard/migration" element={<AdminMediaMigration />} />
              <Route path="/admin/dashboard/ki-modelle" element={<AdminKIModelle />} />
              <Route path="/admin/dashboard/labs" element={<LabsUebersicht />} />
              <Route path="/admin/dashboard/labs/:key" element={<LabsFunktion />} />
              <Route path="/admin/dashboard/suche" element={<AdminSuchverwaltung />} />
              <Route path="/admin/dashboard/thinkific-dozenten" element={<AdminThinkificInstructors />} />
              <Route path="/admin/dashboard/thinkific-bundles" element={<AdminThinkificBundles />} />
              <Route path="/admin/dashboard/transaktionen" element={<AdminTransaktionen />} />
              <Route path="/admin/dashboard/rechnungskorrektur" element={<AdminRechnungskorrektur />} />
              <Route path="/admin/dashboard/zahlungsprobleme" element={<AdminZahlungsprobleme />} />
              <Route path="/admin/dashboard/mahnwesen" element={<AdminMahnwesen />} />
              <Route path="/admin/dashboard/abos-unbezahlt" element={<Navigate to="/admin/dashboard/zahlungsprobleme?reiter=abos" replace />} />
              <Route path="/admin/dashboard/payment-fehler" element={<Navigate to="/admin/dashboard/zahlungsprobleme?reiter=kassenfehler" replace />} />
              <Route path="/admin/dashboard/umsatz" element={<AdminUmsatz />} />
              <Route path="/admin/dashboard/profil-statistiken" element={<AdminProfilStatistiken />} />
              <Route path="/admin/dashboard/abo-einstellungen" element={<AdminAboEinstellungen />} />
              <Route path="/admin/dashboard/ausbildung-teilnehmer" element={<AdminAusbildungTeilnehmer />} />
              <Route path="/admin/dashboard/provisionen" element={<AdminProvisionen />} />
              <Route path="/admin/dashboard/ausbildung-bewertungen" element={<AdminAusbildungBewertungen />} />
              <Route path="/admin/dashboard/meine-provisionen" element={<AdminMeineProvisionen />} />
              <Route path="/admin/dashboard/ausbildung-teilnehmer/import" element={<AdminAusbildungTeilnehmerImport />} />
              <Route path="/admin/dashboard/ausbildung-teilnehmer/legacy-konsolidierung" element={<AdminAusbildungTeilnehmerConsolidation />} />
              <Route path="/admin/dashboard/ausbildung/teilnehmer/:userId" element={<AdminAusbildungTeilnehmerDetail />} />
              <Route path="/admin/dashboard/konsistenz-report" element={<AdminKonsistenzReport />} />
              <Route path="/admin/dashboard/laeuft-alles" element={<AdminLaeuftAlles />} />
              <Route path="/admin/dashboard/kurs-rotation" element={<AdminKursRotation />} />
              <Route path="/admin/dashboard/didacta-stats" element={<AdminDidactaStats />} />
              <Route path="/admin/dashboard/new-user-stats" element={<AdminNewUserStats />} />
              <Route path="/admin/dashboard/mitglieder-stats" element={<AdminMitgliederStats />} />
              <Route path="/admin/dashboard/mitglieder-abos" element={<AdminMitgliederAbos />} />
              <Route path="/admin/dashboard/landing-pages" element={<AdminLandingPages />} />
              <Route path="/admin/dashboard/angebote" element={<AdminAngebote />} />
              <Route path="/admin/dashboard/rueckgewinnung" element={<AdminRueckgewinnung />} />
              <Route path="/admin/dashboard/kontakte" element={<AdminKontakte />} />
              <Route path="/admin/dashboard/kontakte/:id" element={<AdminKontaktAkte />} />
              <Route path="/admin/dashboard/kontakte/konto/:userId" element={<AdminKontaktAkte />} />
              <Route path="/admin/dashboard/flows" element={<AdminFlows />} />
              <Route path="/admin/dashboard/infoveranstaltungen" element={<AdminInfoveranstaltungen />} />
              <Route path="/admin/dashboard/infoveranstaltungen/:id" element={<AdminInfoveranstaltung />} />
              <Route path="/admin/dashboard/pipeline" element={<AdminPipeline />} />
              <Route path="/admin/dashboard/einrichtungen" element={<AdminEinrichtungen />} />
              <Route path="/admin/dashboard/einrichtungen/:id" element={<AdminEinrichtungAkte />} />
              <Route path="/admin/dashboard/probe-abo" element={<AdminProbeAbo />} />
              <Route path="/admin/dashboard/einzelprodukte" element={<Navigate to="/admin/dashboard/umsatz?reiter=produkte" replace />} />
              <Route path="/admin/dashboard/hilfe-artikel" element={<AdminHelpArticles />} />
              <Route path="/admin/dashboard/support-tickets" element={<AdminSupportTickets />} />
              <Route path="/admin/dashboard/support-tickets/:id" element={<AdminTicketDetail />} />
              <Route path="/admin/dashboard/community" element={<AdminCommunity />} />
              <Route path="/admin/dashboard/blog" element={<AdminBlog />} />
            </Route>
            {/* Admin-Rahmen Ende */}
            <Route path="/fortbildung/:slug/live" element={<FortbildungPresent />} />
            <Route path="/giveaway/:slug" element={<Giveaway />} />
            {/* Legacy route redirect */}
            <Route path="/admin/dashboard/thinkific-import" element={<Navigate to="/admin/dashboard/thinkific-kurse" replace />} />
            <Route path="/admin/dashboard/aufgaben" element={<Navigate to="/admin/dashboard/planungszentrale" replace />} />
            <Route path="/admin/dashboard/aufgaben-board" element={<Navigate to="/admin/dashboard/planungszentrale" replace />} />
            {/* Student Dashboard - nur im Hintergrund, kein öffentlicher Login */}
            <Route path="/mein-bereich" element={<StudentDashboard />} />
            <Route path="/mein-bereich/partner" element={<StudentPartnerprogramm />} />
            <Route path="/mein-bereich/kurse" element={<StudentCourses />} />
            <Route path="/mein-bereich/kurs/:slug" element={<StudentCourseView />} />
            <Route path="/mein-bereich/suche" element={<StudentSearch />} />
            <Route path="/mein-bereich/favoriten" element={<StudentFavorites />} />
            <Route path="/mein-bereich/notizen" element={<Navigate to="/mein-bereich/favoriten" replace />} />
            <Route path="/mein-bereich/zertifikate" element={<StudentCertificates />} />
            <Route path="/mein-bereich/lernreise" element={<StudentJourneyPage />} />
            <Route path="/mein-bereich/themenreisen" element={<StudentThemenreisenOverview />} />
            <Route path="/mein-bereich/themenreise/:slug" element={<StudentThemenreise />} />
            <Route path="/mein-bereich/termine" element={<StudentTermine />} />
            <Route path="/mein-bereich/community" element={<StudentCommunity />} />
            <Route path="/mein-bereich/community/mitglieder" element={<CommunityMembers />} />
            <Route path="/mein-bereich/einstellungen" element={<StudentSettings />} />
            <Route path="/termin-gebucht" element={<WebseiteWeiche alt={<TerminGebucht />} neu={<WebseiteTerminDanke />} />} />
            <Route path="/mein-bereich/bereich/:slug" element={<StudentLearningArea />} />
            <Route path="/mein-bereich/musik-kita" element={<Navigate to="/mein-bereich/bereich/musik-kita" replace />} />
            <Route path="/mein-bereich/kategorie/:slug" element={<StudentCategory />} />
            {/* Miko AI Routes */}
            <Route path="/mein-bereich/miko" element={<NeueAnsichtWerkzeugRoute ziel="/mein-bereich/miko/tools" />} />
            <Route path="/mein-bereich/miko/tools" element={<MikoTools />} />
            <Route path="/mein-bereich/miko/chat" element={<MikoChatbot />} />
            <Route path="/mein-bereich/miko/aktionstablett" element={<MikoAktionstablett />} />
            <Route path="/mein-bereich/miko/tools/angebots-assistent" element={<MikoAngebotsAssistent />} />
            <Route path="/mein-bereich/miko/tools/eingewoehnung-gestalten" element={<MikoEingewoehnung />} />
            <Route path="/mein-bereich/miko/tools/geraeusche-safari" element={<MikoGeraeuscheSafari />} />
            <Route path="/mein-bereich/miko/tools/geschichtensaeckchen-erfinder" element={<MikoGeschichtensaeckchen />} />
            <Route path="/mein-bereich/miko/tools/klanggeschichten-zauber" element={<MikoKlanggeschichten />} />
            <Route path="/mein-bereich/miko/tools/musik-mit-naturmaterialien" element={<MikoNaturmaterialien />} />
            <Route path="/mein-bereich/miko/tools/musik-verbindet-kulturen" element={<MikoKulturen />} />
            <Route path="/mein-bereich/miko/tools/rhythmus-detektive" element={<MikoRhythmusDetektive />} />
            <Route path="/mein-bereich/miko/tools/video-ideen-generator" element={<MikoVideoIdeen />} />
            <Route path="/mein-bereich/miko/tools/upload-ideen-generator" element={<MikoUploadIdeen />} />
            <Route path="/mein-bereich/miko/tools/fussball-coach" element={<MikoFussballGenerator />} />
            <Route path="/mein-bereich/miko/bibliothek" element={<MikoBibliothek />} />
            {/* Miko Labs: Mikos neue Werkzeuge in der neuen Ansicht (Schalter, docs/labs.md) */}
            <Route path="/mein-bereich/miko/:werkzeug" element={<NeueAnsichtWerkzeugRoute />} />
            <Route path="/mein-bereich/angebote" element={<NeueAnsichtWerkzeugRoute ziel="/mein-bereich/miko/bibliothek" />} />
            <Route path="/mein-bereich/sammlung" element={<NeueAnsichtWerkzeugRoute ziel="/mein-bereich/favoriten" />} />
            {/* Help System Routes */}
            <Route path="/mein-bereich/hilfe" element={<StudentHelp />} />
            <Route path="/mein-bereich/hilfe/artikel/:slug" element={<StudentHelpArticle />} />
            <Route path="/mein-bereich/hilfe/tickets" element={<StudentTickets />} />
            <Route path="/mein-bereich/hilfe/tickets/neu" element={<StudentCreateTicket />} />
            <Route path="/mein-bereich/hilfe/tickets/:id" element={<StudentTicketDetail />} />
            {/* Admin Help System Routes */}
            <Route path="/webinar/slides" element={<WebinarSlides />} />
            <Route path="/kurs/:slug" element={<WebseiteWeiche alt={<CourseLandingPage />} neu={<WebseiteKursseite />} />} />
            <Route path="/checkout/:courseSlug" element={<WebseiteWeiche alt={<Checkout />} neu={<WebseiteKursKasse />} />} />
            <Route path="/termin-checkout/:terminSlug" element={<WebseiteWeiche alt={<TerminCheckout />} neu={<WebseiteTerminKasse />} />} />
            <Route path="/themenreise-checkout/:slug" element={<WebseiteWeiche alt={<ThemenreiseCheckout />} neu={<WebseiteThemenreiseKasse />} />} />

            <Route path="/abo-checkout/:tier" element={<WebseiteWeiche alt={<AboCheckout />} neu={<WebseiteAboKasse />} />} />
            <Route path="/webinar-preis/*" element={<WebinarKasse />} />
            <Route path="/kurs-vorschau/:slug/:token" element={<KursVorschau />} />
            <Route path="/kauf-erfolgreich" element={<WebseiteWeiche alt={<KaufErfolgreich />} neu={<WebseiteKursDanke />} />} />
            <Route path="/abo-erfolgreich" element={<WebseiteWeiche alt={<AboErfolgreich />} neu={<WebseiteAboDanke />} />} />
              {/* Weggefallene Seiten (Frage 292, Sitzung „Aufräumen“, 14.10.2026): alte Links landen auf der
                  Nachfolgeseite. /test und /test-kurs-slide sind ganz weg (404). Abgelaufene Aktionen mit eigener
                  Seite zeigen „Die Aktion ist beendet“ (Didacta, Comeback, Gewinnspiel, Kita-Testzugang). */}
              <Route path="/startseite-neu" element={<Weiterleitung nach="/" />} />
              <Route path="/entdecken" element={<Weiterleitung nach="/kostenlos" />} />
              <Route path="/kitas" element={<Weiterleitung nach="/musik-kita" />} />
              <Route path="/musikonzeptag-angebot" element={<Weiterleitung nach="/musikonzeptag" />} />
              <Route path="/musikonzeptag/anmeldebestaetigung" element={<Weiterleitung nach="/musikonzeptag" />} />
              <Route path="/pitch" element={<Weiterleitung nach="/" />} />
              <Route path="/pitch2" element={<Weiterleitung nach="/" />} />
              <Route path="/pitch2-geschenk" element={<Weiterleitung nach="/" />} />
              <Route path="/shop-geschenk" element={<Weiterleitung nach="/shopify-danke" />} />
              <Route path="/kursleiter" element={<Weiterleitung nach="/ueber-uns" />} />
              {/* Legacy Thinkific Redirects */}
              <Route path="/courses/*" element={<LegacyRedirects />} />
              <Route path="/pages/*" element={<LegacyPageRedirects />} />
              <Route path="/collections/*" element={<Navigate to="/kurse" replace />} />
              <Route path="/account/*" element={<Navigate to="/mein-bereich" replace />} />
              <Route path="/orders/*" element={<Navigate to="/mein-bereich" replace />} />
              <Route path="/users/sign_in" element={<Navigate to="/login" replace />} />
              <Route path="/sign_in" element={<Navigate to="/login" replace />} />
              <Route path="/sign_up" element={<Navigate to="/registrieren" replace />} />
              <Route path="/users/sign_up" element={<Navigate to="/registrieren" replace />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
              </ErrorBoundary>
            </AccessProvider>
          </SubscriptionProvider>
        </HelmetProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </BrowserRouter>
);

export default App;
